import React, { useState } from 'react';
import { X, Calendar, Download } from 'lucide-react';
import api from '../api/client';
import * as XLSX from 'xlsx';

interface ExportStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
}

const ExportStatsModal: React.FC<ExportStatsModalProps> = ({ isOpen, onClose, companyId, companyName }) => {
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const safeGet = async (url: string, params?: any) => {
    try {
      const res = await api.get(url, { params });
      return res.data;
    } catch {
      return null;
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Выберите даты периода');
      return;
    }

    setLoading(true);
    setError(null);

    const [
      statsData,
      candidates,
      vacancies,
      employees,
      tasks,
      requisitions,
      clients,
      tickets,
      stages
    ] = await Promise.all([
      safeGet('/reports/company-stats', { startDate, endDate }),
      safeGet('/candidates'),
      safeGet('/vacancies'),
      safeGet('/employees'),
      safeGet('/assigned-tasks'),
      safeGet('/requisitions'),
      safeGet('/clients'),
      safeGet('/tickets'),
      safeGet('/candidate-stages')
    ]);

    // Справочники
    const vacancyMap = new Map<string, string>();
    const clientMap = new Map<string, string>();
    const stageMap = new Map<string, string>();

    if (vacancies && Array.isArray(vacancies)) {
      vacancies.forEach((v: any) => {
        const id = v._id || v.id;
        if (id) vacancyMap.set(id, v.title || '');
      });
    }
    if (clients && Array.isArray(clients)) {
      clients.forEach((c: any) => {
        const id = c._id || c.id;
        if (id) clientMap.set(id, c.name || '');
      });
    }
    if (stages && Array.isArray(stages)) {
      stages.forEach((s: any) => {
        const id = s._id || s.id;
        if (id) stageMap.set(id, s.title || '');
      });
    }

    const workbook = XLSX.utils.book_new();

    // ========== ЛИСТ 1: КАНДИДАТЫ ==========
    if (candidates && Array.isArray(candidates)) {
      const data = candidates.map((c: any) => {
        let stageName = '';
        const stageId = c.stage;
        if (stageId) {
          stageName = stageMap.get(stageId) || stageId;
        } else if (c.pipelineStage) {
          const ps = typeof c.pipelineStage === 'string' ? c.pipelineStage : c.pipelineStage._id || c.pipelineStage.id;
          stageName = stageMap.get(ps) || (c.pipelineStage.title || c.pipelineStage.name || ps);
        }

        const vacancyNames: string[] = [];
        if (c.vacancyLinks && Array.isArray(c.vacancyLinks)) {
          c.vacancyLinks.forEach((link: any) => {
            const vid = typeof link === 'string' ? link : link.vacancyId || link._id || link.id;
            if (vid) vacancyNames.push(vacancyMap.get(vid) || vid);
          });
        }
        if (c.vacancyId) {
          const vid = typeof c.vacancyId === 'string' ? c.vacancyId : c.vacancyId._id || c.vacancyId.id;
          if (vid && !vacancyNames.includes(vacancyMap.get(vid) || vid)) {
            vacancyNames.push(vacancyMap.get(vid) || vid);
          }
        }

        return {
          'Имя': c.firstName || '',
          'Фамилия': c.lastName || '',
          'Email': c.email || '',
          'Телефон': c.phone || '',
          'Должность': c.position || '',
          'Источник': c.source || '',
          'Этап': stageName,
          'Вакансии': vacancyNames.join(', '),
          'Теги': c.tags?.join(', ') || '',
          'Комментарии': c.comments?.map((cm: any) => cm.text).join(' | ') || '',
          'Создан': c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '',
          'Обновлён': c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : ''
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Кандидаты');
    }

    // ========== ЛИСТ 2: ВАКАНСИИ ==========
    if (vacancies && Array.isArray(vacancies)) {
      const data = vacancies.map((v: any) => {
        let clientName = v.clientName || '';
        if (!clientName && v.clientId) {
          const cid = typeof v.clientId === 'string' ? v.clientId : v.clientId._id || v.clientId.id;
          clientName = clientMap.get(cid) || cid;
        }
        return {
          'Название': v.title || '',
          'Департамент': v.department || '',
          'Статус': v.status || '',
          'Зарплата от': v.salaryMin,
          'Зарплата до': v.salaryMax,
          'Рекрутеры': v.responsibleRecruiters?.join(', ') || '',
          'Клиент': clientName,
          'Создана': v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ''
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Вакансии');
    }

    // ========== ЛИСТ 3: СОТРУДНИКИ ==========
    if (employees && Array.isArray(employees)) {
      const data = employees.map((e: any) => ({
        'Имя': e.firstName || '',
        'Фамилия': e.lastName || '',
        'Email': e.email || '',
        'Должность': e.position || '',
        'Отдел': e.department || '',
        'Дата найма': e.hireDate ? new Date(e.hireDate).toLocaleDateString() : '',
        'Остаток отпуска': e.leaveBalance?.annual?.total ? `${e.leaveBalance.annual.total} дн.` : ''
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Сотрудники');
    }

    // ========== ЛИСТ 4: ЗАДАЧИ ==========
    if (tasks && Array.isArray(tasks)) {
      const data = tasks.map((t: any) => ({
        'Название': t.title || '',
        'Статус': t.status || '',
        'Приоритет': t.priority || '',
        'Дедлайн': t.dueDate || '',
        'Исполнитель': t.assigneeName || '',
        'Создатель': t.creatorName || '',
        'Создана': t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Задачи');
    }

    // ========== ЛИСТ 5: ЗАЯВКИ НА ПОДБОР ==========
    if (requisitions && Array.isArray(requisitions)) {
      const data = requisitions.map((r: any) => {
        let clientName = r.clientName || '';
        if (!clientName && r.clientId) {
          const cid = typeof r.clientId === 'string' ? r.clientId : r.clientId._id || r.clientId.id;
          clientName = clientMap.get(cid) || cid;
        }
        return {
          'Название': r.title || '',
          'Департамент': r.department || '',
          'Статус': r.status || '',
          'Количество': r.count,
          'Нанято': r.hiredCount,
          'Менеджер': r.managerName || '',
          'Клиент': clientName,
          'Создана': r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Заявки на подбор');
    }

    // ========== ЛИСТ 6: КЛИЕНТЫ ==========
    if (clients && Array.isArray(clients)) {
      const clientVacancyStats: Record<string, { total: number; open: number; closed: number; other: number }> = {};
      if (vacancies && Array.isArray(vacancies)) {
        vacancies.forEach((v: any) => {
          const cid = typeof v.clientId === 'string' ? v.clientId : v.clientId?._id || v.clientId?.id;
          if (!cid) return;
          if (!clientVacancyStats[cid]) clientVacancyStats[cid] = { total: 0, open: 0, closed: 0, other: 0 };
          clientVacancyStats[cid].total++;
          const status = (v.status || '').toLowerCase();
          if (['открыта', 'active', 'open', 'in_progress', 'search', 'offer'].includes(status))
            clientVacancyStats[cid].open++;
          else if (['закрыта', 'closed', 'placement', 'filled'].includes(status))
            clientVacancyStats[cid].closed++;
          else
            clientVacancyStats[cid].other++;
        });
      }
      const data = clients.map((c: any) => {
        const cid = c._id || c.id;
        const stats = clientVacancyStats[cid] || { total: 0, open: 0, closed: 0, other: 0 };
        return {
          'Название': c.name || '',
          'Контактное лицо': c.contactPerson || '',
          'Телефон': c.phone || '',
          'Email': c.email || '',
          'Статус': c.status || '',
          'Всего вакансий': stats.total,
          'Открытых': stats.open,
          'Закрытых': stats.closed,
          'Прочих': stats.other,
          'Создан': c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''
        };
      });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Клиенты');
    }

    // ========== ЛИСТ 7: ТИКЕТЫ ==========
    if (tickets && Array.isArray(tickets)) {
      const data = tickets.map((t: any) => ({
        'Тема': t.subject || '',
        'Пользователь': t.userName || '',
        'Email': t.userEmail || '',
        'Статус': t.status || '',
        'Сообщение': t.message || '',
        'Ответы': t.replies?.map((r: any) => r.message).join(' | ') || '',
        'Создан': t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''
      }));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Тикеты');
    }

    // ========== ЛИСТ 8: АНАЛИТИКА (МАКСИМАЛЬНО ПОЛНАЯ) ==========
    const rows: any[] = [];
    rows.push({ Параметр: `Отчёт по компании: ${companyName}`, Значение: `Период: ${startDate} – ${endDate}` });
    rows.push({});

    const isOpenStatus = (s: string) =>
      ['открыта', 'active', 'open', 'in_progress', 'search', 'offer'].includes(s?.toLowerCase());

    const openVacanciesCount = vacancies?.filter((v: any) => isOpenStatus(v.status)).length || 0;

    // ОСНОВНЫЕ ПОКАЗАТЕЛИ (выводятся один раз)
    rows.push({ Параметр: '📊 ОСНОВНЫЕ ПОКАЗАТЕЛИ', Значение: '' });
    rows.push({ Параметр: 'Сотрудников', Значение: employees?.length || statsData?.employeesCount || 0 });
    rows.push({ Параметр: 'Открытых вакансий', Значение: openVacanciesCount });
    rows.push({ Параметр: 'Кандидатов', Значение: candidates?.length || statsData?.candidatesCount || 0 });
    rows.push({ Параметр: 'Активных заявок на подбор', Значение: statsData?.activeRequisitions || requisitions?.length || 0 });
    rows.push({ Параметр: 'Средний срок найма (дней)', Значение: statsData?.avgTimeToHire || '' });
    rows.push({ Параметр: 'Конверсия в наём (%)', Значение: statsData?.conversionRate || '' });
    if (statsData?.openFee !== undefined) rows.push({ Параметр: 'Гонорар по открытым вакансиям', Значение: statsData.openFee });
    if (statsData?.closedFee !== undefined) rows.push({ Параметр: 'Гонорар по закрытым вакансиям', Значение: statsData.closedFee });

    // ВОРОНКА ПОДБОРА (всегда строим по кандидатам)
    if (candidates && Array.isArray(candidates)) {
      const stageCounts: Record<string, number> = {};
      candidates.forEach((c: any) => {
        const stageId = c.stage;
        const name = stageId ? (stageMap.get(stageId) || stageId) : 'Без этапа';
        stageCounts[name] = (stageCounts[name] || 0) + 1;
      });
      // Если есть данные из statsData.funnel, используем их; иначе выводим свои
      if (statsData?.funnel?.length) {
        rows.push({});
        rows.push({ Параметр: '🔄 ВОРОНКА ПОДБОРА', Значение: '' });
        statsData.funnel.forEach((item: any) => {
          rows.push({ Параметр: item.stage, Значение: item.count });
        });
      } else if (Object.keys(stageCounts).length > 0) {
        rows.push({});
        rows.push({ Параметр: '🔄 ВОРОНКА ПОДБОРА (по кандидатам)', Значение: '' });
        Object.entries(stageCounts).forEach(([stage, count]) => {
          rows.push({ Параметр: stage, Значение: count });
        });
      }
    }

    // ДИНАМИКА ПРИЁМА И УВОЛЬНЕНИЙ
    if (statsData?.monthlyHireFire?.length) {
      rows.push({});
      rows.push({ Параметр: '📅 ДИНАМИКА ПРИЁМА И УВОЛЬНЕНИЙ', Значение: '' });
      statsData.monthlyHireFire.forEach((item: any) => {
        rows.push({ Параметр: item.month, Значение: `Принято: ${item.hired}, Уволено: ${item.left}` });
      });
    } else if (employees && Array.isArray(employees)) {
      const monthly: Record<string, { hired: number; left: number }> = {};
      employees.forEach((e: any) => {
        if (e.hireDate) {
          const m = new Date(e.hireDate).toLocaleString('ru', { month: 'short', year: 'numeric' });
          if (!monthly[m]) monthly[m] = { hired: 0, left: 0 };
          monthly[m].hired++;
        }
        if (e.terminationDate) {
          const m = new Date(e.terminationDate).toLocaleString('ru', { month: 'short', year: 'numeric' });
          if (!monthly[m]) monthly[m] = { hired: 0, left: 0 };
          monthly[m].left++;
        }
      });
      if (Object.keys(monthly).length > 0) {
        rows.push({});
        rows.push({ Параметр: '📅 ДИНАМИКА ПРИЁМА И УВОЛЬНЕНИЙ (расчёт)', Значение: '' });
        Object.entries(monthly).forEach(([m, v]) => {
          rows.push({ Параметр: m, Значение: `Принято: ${v.hired}, Уволено: ${v.left}` });
        });
      }
    }

    // СОТРУДНИКИ ПО ОТДЕЛАМ
    if (statsData?.departmentDistribution && Object.keys(statsData.departmentDistribution).length) {
      rows.push({});
      rows.push({ Параметр: '🏢 СОТРУДНИКИ ПО ОТДЕЛАМ', Значение: '' });
      Object.entries(statsData.departmentDistribution).forEach(([dept, count]) => {
        rows.push({ Параметр: dept, Значение: count });
      });
    } else if (employees && Array.isArray(employees)) {
      const deptCounts: Record<string, number> = {};
      employees.forEach((e: any) => {
        const d = e.department || 'Без отдела';
        deptCounts[d] = (deptCounts[d] || 0) + 1;
      });
      rows.push({});
      rows.push({ Параметр: '🏢 СОТРУДНИКИ ПО ОТДЕЛАМ (расчёт)', Значение: '' });
      Object.entries(deptCounts).forEach(([d, c]) => rows.push({ Параметр: d, Значение: c }));
    }

    // ИСТОЧНИКИ КАНДИДАТОВ
    if (statsData?.sourceDistribution && Object.keys(statsData.sourceDistribution).length) {
      rows.push({});
      rows.push({ Параметр: '📌 ИСТОЧНИКИ КАНДИДАТОВ', Значение: '' });
      Object.entries(statsData.sourceDistribution).forEach(([source, count]) => {
        rows.push({ Параметр: source, Значение: count });
      });
    } else if (candidates && Array.isArray(candidates)) {
      const sourceCounts: Record<string, number> = {};
      candidates.forEach((c: any) => {
        const s = c.source || 'не указан';
        sourceCounts[s] = (sourceCounts[s] || 0) + 1;
      });
      rows.push({});
      rows.push({ Параметр: '📌 ИСТОЧНИКИ КАНДИДАТОВ (расчёт)', Значение: '' });
      Object.entries(sourceCounts).forEach(([s, c]) => rows.push({ Параметр: s, Значение: c }));
    }

    // СТАТУСЫ ЗАЯВОК НА ПОДБОР
    if (statsData?.requisitionStatuses && Object.keys(statsData.requisitionStatuses).length) {
      rows.push({});
      rows.push({ Параметр: '📋 СТАТУСЫ ЗАЯВОК НА ПОДБОР', Значение: '' });
      Object.entries(statsData.requisitionStatuses).forEach(([status, count]) => {
        rows.push({ Параметр: status, Значение: count });
      });
    }

    // БЛИЖАЙШИЕ ОТПУСКА
    if (statsData?.upcomingLeaves?.length) {
      rows.push({});
      rows.push({ Параметр: '🌴 БЛИЖАЙШИЕ ОТПУСКА', Значение: '' });
      statsData.upcomingLeaves.forEach((leave: any) => {
        rows.push({
          Параметр: leave.employeeName,
          Значение: `${new Date(leave.startDate).toLocaleDateString()} – ${new Date(leave.endDate).toLocaleDateString()}`,
        });
      });
    }

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Аналитика');

    if (workbook.SheetNames.length === 0) {
      setError('Нет доступных данных для экспорта.');
      setLoading(false);
      return;
    }

    XLSX.writeFile(workbook, `CRM_${companyName}_${startDate}_${endDate}.xlsx`);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}><Download size={18} style={{ marginRight: '8px' }} />Полная выгрузка HRM</h3>
          <button onClick={onClose} style={styles.closeButton}><X size={20} /></button>
        </div>
        <div style={styles.body}>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>Будут выгружены все доступные разделы.</p>
          <div style={styles.dateGroup}>
            <label style={styles.label}><Calendar size={14} style={{ marginRight: '6px' }} />Дата начала</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={styles.input} />
          </div>
          <div style={styles.dateGroup}>
            <label style={styles.label}><Calendar size={14} style={{ marginRight: '6px' }} />Дата окончания</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={styles.input} />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button onClick={handleExport} disabled={loading} style={styles.exportButton}>
            {loading ? 'Формирование...' : 'Сформировать Excel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(20px)', borderRadius: '28px', padding: '28px', width: '500px', maxWidth: '90%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.5)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '20px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  closeButton: { background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '40px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#4a4a68', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
  body: { display: 'flex', flexDirection: 'column', gap: '18px' },
  dateGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '14px', fontWeight: 500, color: '#1a1a2e', display: 'flex', alignItems: 'center' },
  input: { padding: '12px 16px', borderRadius: '40px', border: '1px solid rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)', fontSize: '14px', outline: 'none', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' },
  error: { color: '#ff4d4f', fontSize: '13px', textAlign: 'center', padding: '8px', background: 'rgba(255,77,79,0.1)', borderRadius: '40px', backdropFilter: 'blur(4px)' },
  exportButton: { background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: 'white', border: 'none', borderRadius: '40px', padding: '12px 24px', fontSize: '16px', fontWeight: 600, cursor: 'pointer', marginTop: '8px', transition: 'all 0.2s', boxShadow: '0 8px 20px rgba(37,117,252,0.3)' },
};

export default ExportStatsModal;
